import { css } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives';

import wacToken from '@/libraries/design/foundations/token/src/wac-token';

import { SearchResultTryButton } from './search-result-try-button';

type ResultProps = {
	index: number;
	title: string;
	description: string;
	href: string;
	objectId: string;
	queryId: string;
	onSearchLinkClick: (objectId: string, position: number) => void;
	isExperimentVariant?: boolean;
	showTryButton?: boolean;
	searchQuery?: string;
};

/* eslint-disable complexity -- pre-existing complexity (15); +2 from showTryButton prop */
export const Result = ({
	index,
	title,
	description,
	href,
	objectId,
	queryId,
	onSearchLinkClick,
	isExperimentVariant = false,
	showTryButton = false,
	searchQuery = '',
}: ResultProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const decodeHtml = (html: string): string => {
		const txt = document.createElement('textarea');
		txt.innerHTML = html;
		return txt.value;
	};
	const handleSearchResultClick = (
		objectId: string,
		index: number,
		linkUrl: string,
		queryId: string,
	) => {
		onSearchLinkClick(objectId, index);
		createAnalyticsEvent({
			eventName: 'searchResult',
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'linkButton',
			actionSubjectId: 'searchResults',
			source: 'searchPage',
			eventContainer: 'searchResults',
			attributes: {
				product: 'search',
				objectId,
				linkUrl,
				rank: index,
				queryId,
			},
		}).fire();
	};

	const isNonVariantFirstResult = !isExperimentVariant && index === 0;
	const isNonVariantOtherResult = !isExperimentVariant && index !== 0;

	return (
		<div
			css={[
				isExperimentVariant && variantResultContainerCss,
				isNonVariantFirstResult && resultContainerCss,
				isNonVariantOtherResult && resultContainerTopCss,
			]}
		>
			<a
				data-testid={`result-id-${index}`}
				href={href}
				css={[isExperimentVariant && variantResultLinkCss, !isExperimentVariant && resultLinkCss]}
				onClick={(event) => {
					const relativeUrlPath = href.split('https://www.atlassian.com')[1] || href;

					handleSearchResultClick(objectId, index + 1, href, queryId);

					event.preventDefault();
					window.location.href = relativeUrlPath;
				}}
			>
				<h3
					css={[
						isExperimentVariant && variantResultLinkTextCss,
						!isExperimentVariant && resultLinkTextCss,
					]}
				>
					{decodeHtml(title)}
				</h3>
			</a>
			<span
				css={[
					isExperimentVariant && variantResultDescriptionCss,
					!isExperimentVariant && resultDescriptionCss,
				]}
			>
				{decodeHtml(description)}
			</span>
			<span
				css={[isExperimentVariant && variantResultUrlCss, !isExperimentVariant && resultUrlCss]}
			>
				{href}
			</span>
			{showTryButton && (
				<SearchResultTryButton resultUrl={href} searchQuery={searchQuery} resultIndex={index} />
			)}
		</div>
	);
};
/* eslint-enable complexity */

const resultContainerCss = css({
	paddingBlock: '30px',
	width: '100%',
	[media.below.sm]: {
		paddingBlock: '15px',
	},
});

const resultContainerTopCss = css({
	paddingBlock: '30px',
	width: '100%',
	borderBlockStart: `1px solid ${wacToken('color.neutral.light0')}`,
	[media.below.sm]: {
		paddingBlock: '15px',
	},
});

const variantResultContainerCss = css({
	width: '100%',
	paddingBlock: '18px',

	[media.below.lg]: {
		paddingBlock: '16px',
	},

	[media.below.sm]: {
		paddingBlock: '12px',
	},
});

const resultLinkCss = css({
	color: wacToken('color.primary.blue'),
	borderBlockEnd: '1px solid transparent',
	display: 'inline-block',

	':hover': {
		borderBlockEnd: `1px solid ${wacToken('color.primary.blue')}`,
	},

	':visited': {
		color: wacToken('color.primary.purple'),
	},

	':hover:visited': {
		borderBlockEnd: `1px solid ${wacToken('color.primary.purple')}`,
	},
});

const variantResultLinkCss = css({
	color: wacToken('color.primary.blue'),
	borderBlockEnd: '1px solid transparent',
	display: 'inline-block',

	':hover': {
		color: wacToken('color.extended.blue800'),
	},

	':visited': {
		color: wacToken('color.extended.purple800'),
	},

	':hover:visited': {
		color: wacToken('color.extended.purple900'),
	},
});

const variantResultLinkTextCss = css({
	marginBlock: '0',
	fontSize: '24px',
	[media.below.sm]: {
		fontSize: '20px',
		lineHeight: '26px',
	},
});

const resultLinkTextCss = css({
	marginBlock: '0',
	fontSize: '1.25rem',
	[media.below.sm]: {
		fontSize: '1rem',
	},
});

const variantResultDescriptionCss = css({
	display: 'block',
	color: wacToken('color.secondary.navy'),
	fontSize: '16px',
	lineHeight: '24px',
	marginBlock: '5px 11px',
	[media.below.sm]: {
		fontSize: '14px',
		lineHeight: '20px',
	},
});

const resultDescriptionCss = css({
	display: 'block',
	color: wacToken('color.secondary.navy'),
	fontSize: '14px',
	lineHeight: '20px',
	marginBlock: '5px 11px',
});

const variantResultUrlCss = css({
	display: 'block',
	color: wacToken('color.neutral.light500'),
	fontSize: '14px',
	lineHeight: '20px',
	'word-wrap': 'break-word',
});

const resultUrlCss = css({
	display: 'block',
	color: wacToken('color.neutral.dark700'),
	fontSize: '12px',
	lineHeight: '20px',

	[media.below.sm]: {
		display: 'none',
	},
});
